import { useState, useRef } from "react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { StickyNote, Paperclip, Trash2, Download, Upload, Loader2, FileText, Eye } from "lucide-react";
import { fmt, isImage, isPdf, canPreview } from "./useInternalNotes";

export const NotesSection = ({ notes, loading, saving, onAdd, onDelete }) => {
  const [text, setText] = useState("");
  const submit = async () => { if (text.trim() && await onAdd(text)) setText(""); };
  return (
    <div className="mb-5">
      <div className="flex items-start gap-2 mb-3">
        <Textarea data-testid="note-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Aggiungi una nota interna..." rows={2} className="bg-white" />
        <Button data-testid="add-note-button" onClick={submit} disabled={saving || !text.trim()} className="gap-1.5 shrink-0">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <StickyNote className="h-4 w-4" />} Aggiungi
        </Button>
      </div>
      {loading ? <p className="text-xs text-slate-400">Caricamento...</p>
        : notes.length === 0 ? <p className="text-xs text-slate-400">Nessuna nota interna.</p>
        : (
          <div className="space-y-2">
            {notes.map((n) => (
              <div key={n.id} data-testid={`note-${n.id}`} className="flex items-start justify-between gap-3 bg-white rounded-md border border-slate-200 px-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm text-slate-800 break-words">{n.text}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">{n.author} · {fmt(n.created_at)}</p>
                </div>
                <button data-testid={`del-note-${n.id}`} onClick={() => onDelete(n.id)} className="text-slate-400 hover:text-red-600 transition-colors shrink-0"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}
    </div>
  );
};

export const AttachmentsSection = ({ attachments, uploading, previewLoading, onUpload, onPreview, onDownload, onDelete }) => {
  const fileRef = useRef(null);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (file) await onUpload(file);
    if (fileRef.current) fileRef.current.value = "";
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Paperclip className="h-3.5 w-3.5" /> Allegati</span>
        <input ref={fileRef} type="file" data-testid="attachment-input" onChange={onFile} className="hidden" />
        <Button data-testid="upload-attachment-button" variant="outline" size="sm" className="gap-1.5" onClick={() => fileRef.current?.click()} disabled={uploading}>
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Carica file
        </Button>
      </div>
      {attachments.length === 0 ? <p className="text-xs text-slate-400">Nessun allegato.</p> : (
        <div className="space-y-2">
          {attachments.map((a) => (
            <div key={a.id} data-testid={`attachment-${a.id}`} className="flex items-center gap-3 bg-white rounded-md border border-slate-200 px-3 py-2">
              <FileText className="h-4 w-4 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-800 truncate">{a.filename}</p>
                <p className="text-[11px] text-slate-400">{(a.size / 1024).toFixed(0)} KB · {fmt(a.created_at)}</p>
              </div>
              {canPreview(a.content_type) && (
                <button data-testid={`preview-att-${a.id}`} onClick={() => onPreview(a)} disabled={previewLoading} className="text-slate-400 hover:text-primary transition-colors" title="Anteprima"><Eye className="h-4 w-4" /></button>
              )}
              <button data-testid={`download-att-${a.id}`} onClick={() => onDownload(a)} className="text-slate-400 hover:text-primary transition-colors" title="Scarica"><Download className="h-4 w-4" /></button>
              <button data-testid={`del-att-${a.id}`} onClick={() => onDelete(a.id)} className="text-slate-400 hover:text-red-600 transition-colors" title="Elimina"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const PreviewDialog = ({ preview, onClose }) => (
  <Dialog open={!!preview} onOpenChange={(o) => { if (!o) onClose(); }}>
    <DialogContent className="max-w-3xl">
      <DialogHeader><DialogTitle className="font-heading text-base truncate pr-6" data-testid="preview-title">{preview?.name}</DialogTitle></DialogHeader>
      <div className="w-full" data-testid="preview-body">
        {preview && isImage(preview.type) && <img src={preview.url} alt={preview.name} className="w-full max-h-[70vh] object-contain rounded-md bg-slate-50" />}
        {preview && isPdf(preview.type) && (
          <object data={preview.url} type="application/pdf" className="w-full h-[70vh] rounded-md border border-slate-200" aria-label="anteprima-pdf">
            <iframe title="anteprima-pdf" src={preview.url} className="w-full h-[70vh] rounded-md border border-slate-200" />
          </object>
        )}
        {preview && (
          <div className="mt-3 text-center">
            <a href={preview.url} target="_blank" rel="noopener noreferrer" data-testid="preview-open-tab" className="text-sm text-primary hover:underline">Se l'anteprima non compare, apri in una nuova scheda</a>
          </div>
        )}
      </div>
    </DialogContent>
  </Dialog>
);
